import { useMutation, useQueryClient } from "@tanstack/react-query";

import { notificationsApi } from "../lib/api";
import {
  findNotificationItem,
  listFilter,
  notificationsRootKey,
  patchListForMarkAllRead,
  patchListForMarkRead,
  patchSummaryForMarkRead,
  restoreNotificationQueries,
  snapshotNotificationQueries,
} from "./notificationCacheUpdates";
import { notificationSummaryQueryKey } from "./useNotificationSummary";

export const useNotificationMutations = () => {
  const queryClient = useQueryClient();

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: notificationsRootKey });

      const snapshot = snapshotNotificationQueries(queryClient);
      const readAt = new Date().toISOString();
      const item = findNotificationItem(snapshot.lists, id);

      snapshot.lists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(
          queryKey,
          patchListForMarkRead(data, id, listFilter(queryKey), readAt),
        );
      });

      if (snapshot.summary && item) {
        queryClient.setQueryData(
          notificationSummaryQueryKey,
          patchSummaryForMarkRead(snapshot.summary, item),
        );
      }

      return { snapshot };
    },
    onError: (_error, _id, context) => {
      restoreNotificationQueries(queryClient, context?.snapshot);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsRootKey });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationsRootKey });

      const snapshot = snapshotNotificationQueries(queryClient);
      const readAt = new Date().toISOString();

      snapshot.lists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(
          queryKey,
          patchListForMarkAllRead(data, listFilter(queryKey), readAt),
        );
      });

      if (snapshot.summary) {
        queryClient.setQueryData(notificationSummaryQueryKey, {
          ...snapshot.summary,
          unread: 0,
          read: snapshot.summary.total,
          byCategory: { mention: 0, system: 0 },
        });
      }

      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      restoreNotificationQueries(queryClient, context?.snapshot);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsRootKey });
    },
  });

  const clearRead = useMutation({
    mutationFn: () => notificationsApi.clearRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsRootKey });
    },
  });

  return { markRead, markAllRead, clearRead };
};
